import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtWhen } from "../../lib/peApi";

export default function PEConfirmationPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const kind = params.get("kind"); // cancel | reschedule | undefined (deposit)
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, failed: true });
      return;
    }
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const s = await api(`/payments/status/${sessionId}`);
        if (s.payment_status === "paid" && (s.booking || s.kind !== "deposit")) {
          setState({ loading: false, ...s });
          clearInterval(timer);
        } else if (tries > 12 || ["failed", "expired"].includes(s.status)) {
          setState({ loading: false, failed: true });
          clearInterval(timer);
        }
      } catch {
        if (tries > 12) {
          setState({ loading: false, failed: true });
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  const b = state.booking;

  return (
    <main id="main-content" data-testid="pe-confirmation" className="min-h-screen bg-pe-ivory flex items-center justify-center px-5 py-24">
      <div className="max-w-lg w-full bg-white border border-pe-gold/40 rounded-sm p-10 text-center shadow-[0_20px_60px_rgba(44,58,40,0.08)]">
        <img src="/images/pe-logo.png" alt="Prismatic Essence" className="h-16 w-auto mx-auto mb-5" />
        {state.loading && <p className="font-pe italic text-xl text-pe-olive">{kind === "cancel" ? "Confirming your cancellation…" : kind === "reschedule" ? "Confirming your new time…" : "Confirming your deposit…"}</p>}
        {state.failed && (
          <>
            <h1 className="font-pe text-3xl text-pe-forest mb-4">We couldn't confirm that payment</h1>
            <p className="text-sm text-pe-body">If your payment was completed, it will appear shortly. Otherwise nothing was charged and your appointment is unchanged — you can try again from your dashboard or the secure guest page.</p>
          </>
        )}
        {b && (
          <>
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-pe-olive mb-3">
              {kind === "cancel" ? "Cancelled" : kind === "reschedule" ? "Rescheduled" : b.status === "confirmed" ? "Confirmed" : "Pending final confirmation"}
            </p>
            <h1 data-testid="pe-confirmation-title" className="font-pe text-3xl text-pe-forest mb-6">
              {kind === "cancel" ? "It's cancelled." : kind === "reschedule" ? "New time, same deposit." : b.status === "confirmed" ? "You're booked." : "Almost there."}
            </h1>
            <div className="text-left text-sm space-y-2.5 border-y border-pe-sand/60 py-5 mb-6">
              <div className="flex justify-between"><span className="text-pe-muted">Guest</span><span>{b.client_name}</span></div>
              <div className="flex justify-between"><span className="text-pe-muted">Service</span><span className="font-pe text-lg">{b.service.name}</span></div>
              {kind === "cancel" ? (
                <>
                  <div className="flex justify-between"><span className="text-pe-muted">Original time</span><span>{fmtWhen(b.start)}</span></div>
                  <div className="flex justify-between"><span className="text-pe-muted">Late-cancellation fee</span><span className="text-pe-brass font-medium">paid ✓</span></div>
                </>
              ) : kind === "reschedule" ? (
                <>
                  <div className="flex justify-between"><span className="text-pe-muted">New time</span><span>{fmtWhen(b.start)}</span></div>
                  <div className="flex justify-between"><span className="text-pe-muted">Late-rescheduling fee</span><span className="text-pe-brass font-medium">paid ✓</span></div>
                </>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-pe-muted">When</span><span>{fmtWhen(b.start)}</span></div>
                  <div className="flex justify-between"><span className="text-pe-muted">Duration</span><span>{b.service.duration_label}</span></div>
                  {b.quote.service_price && <div className="flex justify-between"><span className="text-pe-muted">Service price</span><span>{b.service.starting ? "from " : ""}{fmtMoney(b.quote.service_price)}</span></div>}
                  {(b.quote.addons || []).filter((a) => a.amount > 0).map((a) => (
                    <div key={a.label} className="flex justify-between"><span className="text-pe-muted">{a.group}: {a.label}</span><span>+{fmtMoney(a.amount)}</span></div>
                  ))}
                  {b.quote.addons_total > 0 && <div className="flex justify-between"><span className="text-pe-muted">Estimated total</span><span>{fmtMoney(b.quote.subtotal)}</span></div>}
                  {b.quote.discount && <div className="flex justify-between text-pe-olive"><span>{b.quote.discount.label}</span><span>−{fmtMoney(b.quote.discount.amount)}</span></div>}
                  <div className="flex justify-between"><span className="text-pe-muted">Deposit</span><span className="text-pe-brass font-medium">$25.00 paid ✓</span></div>
                  <div className="flex justify-between"><span className="text-pe-muted">Balance</span><span>{b.quote.pending_confirmation ? "Pending price confirmation — approved by you first" : b.quote.balance !== null && b.quote.balance !== undefined ? fmtMoney(b.quote.balance) : "Confirmed with you before collection"}</span></div>
                </>
              )}
            </div>
            <p className="text-[12px] text-pe-muted leading-relaxed mb-8">
              {kind === "cancel"
                ? "Your cancellation is confirmed and a receipt note is on its way. Your $25 deposit is not automatically refunded or forfeited — the house confirms any deposit handling with you directly."
                : kind === "reschedule"
                  ? "Your appointment moved and your deposit moved with it — no second deposit was collected. Updated reminders will follow."
                  : "A confirmation is on its way to your inbox. Reminders arrive one month, one week and one day before your appointment, as applicable. Your information is handled as described in the privacy notice — never sold, never shared for third-party marketing."}
            </p>
            <a href={b.client_type === "guest" ? "/prismatic-essence/guest" : "/prismatic-essence/account"} data-testid="pe-confirmation-account" className="inline-block rounded-full bg-pe-forest text-pe-ivory px-8 py-3 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors">
              {b.client_type === "guest" ? "Manage via secure guest access" : "Go to your dashboard"}
            </a>
          </>
        )}
      </div>
    </main>
  );
}
